import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { filter, tap } from 'rxjs'
import type { ListBundlesFn } from '@/features/admin'
import type {
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceRolesFn,
  GetWorkspaceSkillsFn,
  ListRolesFn,
  ListSkillsFn,
} from '@/features/iam'
import type {
  FetchInventoryItemExportsFn,
  ImportInventoryItemsFn,
  ListInventoryItemsFn,
} from '@/features/inventory'
import type { QuestionListViewModelFactory } from '@/features/jobs'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { createObjectStorage } from '@/infra/object-storage'
import type { Storage } from '@/infra/storage'
import type { NavigateFn } from '@/RootStore'
import { extractMessageFromError } from '@/utils/error-util'
import { links } from '@/utils/link'
import { tapAction, toStream } from '@/utils/mobx-util'
import { isNotNil } from '@/utils/object-util'
import { clock } from '@/utils/rx-util'
import { validateAll } from '@/utils/validx-util'
import {
  canModifyLegends,
  canViewWorkspaceLegends,
} from '@/features/legends/legend-access'
import type { LegendAnalyzerError } from '@/features/legends/legend-engine'
import type {
  LegendStore,
  SavedLegendVersion,
} from '@/features/legends/LegendStore'
import type { LegendVersion } from '@/features/legends/LegendVersion'
import {
  getAnalyzerErrors,
  inflateAnalyzerErrors,
  mapAnalyzerErrorsToDocumentPaths,
} from '@/features/legends/editor/analyzer-errors'
import type { EntityProvider } from '@/features/legends/editor/entities'
import { LegendDraftPersister } from '@/features/legends/editor/LegendDraftPersister'
import {
  emptyLegendDocument,
  type LegendDocument,
} from '@/features/legends/editor/legend-document'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import {
  firstError,
  type Validatable,
} from '@/features/legends/editor/validation'
import { waitForPaint } from '@/features/legends/editor/wait-for-paint'
import { InfoOptionsViewModel } from '@/features/legends/options/InfoOptionsViewModel'
import { LegendOptionsViewModel } from '@/features/legends/options/LegendOptionsViewModel'
import { LegendPreviewViewModel } from '@/features/legends/preview/LegendPreviewViewModel'
import { QuestionsBuilderViewModel } from '@/features/legends/questions/QuestionsBuilderViewModel'
import type { QuestionEditorViewModelBase } from '@/features/legends/questions/QuestionEditorViewModelBase'
import { formatRelativeTime } from '@/features/legends/relative-time'
import { RoutingEditorViewModel } from '@/features/legends/routing/RoutingEditorViewModel'
import { TemplatesBuilderViewModel } from '@/features/legends/templates/TemplatesBuilderViewModel'
import { LegendTransferViewModel } from '@/features/legends/transfer/LegendTransferViewModel'
import {
  type SelectLegendVersionOptions,
  VersionManagerViewModel,
} from '@/features/legends/versioning/VersionManagerViewModel'
import { LegendSymbolScope } from './LegendSymbolScope'
import { LegendWorkspaceCatalog } from './LegendWorkspaceCatalog'

const optionsTabs = ['General', 'Routing', 'Info', 'Transfer'] as const

/**
 * A tab of the options sidebar.
 */
export type OptionsTab = (typeof optionsTabs)[number]

/**
 * A tab of the editor. The options tabs show under Options.
 */
export type EditorTab = OptionsTab | 'Questions' | 'Templates' | 'Preview'

/**
 * A tab of the editor's header. All options tabs show as Options.
 */
export type HeaderTab = 'Options' | 'Questions' | 'Templates' | 'Preview'

/**
 * Something that is part of the legend's symbol order. Each builder sees the
 * symbols declared by the builders before it.
 */
interface Builder extends Validatable, EntityProvider {}

/**
 * Dependencies of the legend editor.
 */
export interface LegendEditorViewModelOptions {
  legendId: Id
  legendStore: LegendStore
  storage: Storage
  navigate: NavigateFn
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  getCurrentUser: GetCurrentUserFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  listRoles: ListRolesFn
  getWorkspaceRoles: GetWorkspaceRolesFn
  listSkills: ListSkillsFn
  getWorkspaceSkills: GetWorkspaceSkillsFn
  listInventoryItems: ListInventoryItemsFn
  fetchInventoryItemExports: FetchInventoryItemExportsFn
  importInventoryItems: ImportInventoryItemsFn
  listBundles: ListBundlesFn
  createQuestionListViewModel: QuestionListViewModelFactory
  waitForViewTransition: () => Promise<void>
}

/**
 * View model for the legend editor. Holds the legend version being edited,
 * the view models for each of its parts, and what ties the parts together:
 * their symbols, the workspace's catalog, validation and saving.
 */
export class LegendEditorViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private _optionsTab: OptionsTab = 'General'

  /**
   * The tab in the URL, which showing does not navigate to again.
   */
  private _routeTab: EditorTab = 'General'

  /**
   * Aborts the version selection that is loading, so that only the last one
   * loads.
   */
  private _selection = new AbortController()

  private readonly _persister: LegendDraftPersister
  private readonly _partDependencies: LegendPartDependencies

  @observable accessor currentTab: EditorTab = 'General'
  @observable.ref accessor legendVersion: LegendVersion | null = null
  @observable.ref private accessor _initialState: LegendDocument | null = null

  /**
   * Why a part of the loaded version failed to load, if one did.
   */
  @observable accessor hydrationError: string | null = null

  /**
   * The time, kept current so that "saved 2 minutes ago" stays true.
   */
  @observable accessor now = new Date()

  @observable.ref accessor questionsVm!: QuestionsBuilderViewModel
  @observable.ref accessor templatesVm!: TemplatesBuilderViewModel
  @observable.ref accessor optionsVm!: LegendOptionsViewModel
  @observable.ref accessor routingVm!: RoutingEditorViewModel
  @observable.ref accessor infoVm!: InfoOptionsViewModel

  readonly symbols: LegendSymbolScope
  readonly catalog: LegendWorkspaceCatalog
  readonly versionsVm: VersionManagerViewModel
  readonly transferVm: LegendTransferViewModel
  readonly previewVm: LegendPreviewViewModel

  /**
   * The palette that jumps to any element of the legend.
   */
  readonly paletteModal = new ModalViewModel()

  /**
   * Keeps the URL on the current tab. Each tab gets a history entry, so Back
   * returns to the previous tab.
   */
  private readonly _tabChanges$ = toStream(() => this.currentTab).pipe(
    filter((tab) => tab !== this._routeTab),
    tap((tab) =>
      this._options.navigate(
        links.legend(this._options.legendId, tab.toLowerCase()),
      ),
    ),
  )

  /**
   * Saves the changes made to the version being edited.
   */
  private readonly _changes$ = toStream(() =>
    this._persister.target && this.canModify ? this.serialize() : null,
  ).pipe(
    filter(isNotNil),
    tap((serialized) => this._persister.schedule(serialized)),
  )

  private readonly _clock$ = clock(20_000).pipe(
    tapAction((now) => {
      this.now = now
    }),
  )

  constructor(private readonly _options: LegendEditorViewModelOptions) {
    this.symbols = new LegendSymbolScope(() => this.builders)
    this.catalog = new LegendWorkspaceCatalog({
      getMembership: () => this.membership,
      getServiceType: () => this.optionsVm.serviceType,
      getCurrentUser: _options.getCurrentUser,
      listSkills: _options.listSkills,
      getWorkspaceSkills: _options.getWorkspaceSkills,
      listRoles: _options.listRoles,
      getWorkspaceRoles: _options.getWorkspaceRoles,
      listInventoryItems: _options.listInventoryItems,
      listBundles: _options.listBundles,
      createAlertRetryWrapper: _options.createAlertRetryWrapper,
    })
    this._partDependencies = {
      symbols: this.symbols,
      catalog: this.catalog,
      isEditingDisabled: () => this.isEditingDisabled,
    }
    this.versionsVm = new VersionManagerViewModel({
      legendId: _options.legendId,
      legendStore: _options.legendStore,
      createAlertRetryWrapper: _options.createAlertRetryWrapper,
      createAlertErrorHandler: _options.createAlertErrorHandler,
      getSelectedVersion: () => this.legendVersion,
      isDirty: () => this.isDirty,
      isBusy: () => this.isBusy,
      getNow: () => this.now,
      selectLegendVersion: (version, options) =>
        this.selectLegendVersion(version, options),
      persistAndCheck: () => this.persistAndCheck(),
      publish: (versionNotes) => this._publish(versionNotes),
      discardLocalChanges: () => this._discardLocalChanges(),
      revertToSelectedVersion: () => this._revertToSelectedVersion(),
    })
    this.transferVm = new LegendTransferViewModel({
      catalog: this.catalog,
      fetchInventoryItemExports: _options.fetchInventoryItemExports,
      importInventoryItems: _options.importInventoryItems,
      newAlert: _options.newAlert,
      createAlertErrorHandler: _options.createAlertErrorHandler,
      getWorkspace: () => this.workspace,
      getLegendVersion: () => this.legendVersion,
      hasHydrationError: () => this.hydrationError !== null,
      isEditingDisabled: () => this.isEditingDisabled,
      serialize: () => this.serialize(),
      saveNow: (document) => this.saveNow(document),
      selectLegendVersion: (version, options) =>
        this.selectLegendVersion(version, options),
      refreshInventoryItems: () => this.catalog.fetchInventoryItems(),
    })
    this._persister = new LegendDraftPersister({
      legendStore: _options.legendStore,
      isBusy: () => this.isBusy,
      resolveConflict: (lastUpdatedBy, force) =>
        this.versionsVm.conflictVm.resolve(lastUpdatedBy, force),
      onDraftCreated: (version) => this._showCreatedDraft(version),
      createAlertErrorHandler: _options.createAlertErrorHandler,
    })
    this._createBuilders()
    this._manageBuilders()
    this.previewVm = new LegendPreviewViewModel({
      legendStore: _options.legendStore,
      createQuestionListViewModel: _options.createQuestionListViewModel,
      catalog: this.catalog,
      getLegendVersion: () => this.legendVersion,
      getOrganizerBindings: () => this.symbols.organizerBindings,
      getQuestions: () => this.questionsVm.allQuestions,
      getPrepAndReviewConfig: () => this.optionsVm.prepAndReviewConfig,
      getStorage: () => this.storage,
      isShowing: () => this.currentTab === 'Preview',
      showEditor: () => this.setTab('Options'),
    })

    this._lifecycle.manage(this._tabChanges$)
    this._lifecycle.manage(this._persister)
    this._lifecycle.manage(this._changes$)
    this._lifecycle.manage(this._clock$)
    this._lifecycle.manage(this.versionsVm)
    this._lifecycle.manage(this.previewVm)
  }

  /**
   * The user's membership in the legend's workspace.
   */
  @computed
  get membership() {
    // Every version is in the legend's workspace, so the latest one loaded
    // answers before a version is selected.
    const workspaceId = (this.legendVersion ?? this.versionsVm.latestVersion)
      ?.workspaceId
    return (
      this._options
        .getCurrentUserWorkspaceMemberships()
        .find((m) => m.workspace.id === workspaceId) ?? null
    )
  }

  @computed
  get workspace() {
    return this.membership?.workspace ?? null
  }

  @computed
  get isTaxLegend() {
    return this.optionsVm.serviceType === 'Tax'
  }

  /**
   * Local storage for the legend's workspace. The preview keeps its sample
   * input here.
   */
  @computed
  get storage() {
    const slug = this.workspace?.slug
    return createObjectStorage(
      this._options.storage,
      slug ? `${slug}.` : 'global.',
    )
  }

  @computed
  get name() {
    return this.optionsVm.name
  }

  /**
   * The name to show while the legend loads, or `null` when we don't know it
   * yet because the legend list hasn't loaded the legend.
   */
  @computed
  get loadingName() {
    return (
      this._options.legendStore.latestVersionFor(this._options.legendId)
        ?.name ?? null
    )
  }

  /**
   * Whether discarding, reverting, publishing or importing is running.
   */
  @computed
  get isBusy() {
    return this.versionsVm.isChangingVersion || this.transferVm.isBusy
  }

  @computed
  get canView() {
    return (
      !!this.membership &&
      canViewWorkspaceLegends(this.membership, this._options.getCurrentUser())
    )
  }

  /**
   * With permission, and only while every part of the version loaded: saving
   * would replace a part that didn't with the builder's defaults.
   */
  @computed
  get canModify() {
    return (
      !!this.membership &&
      canModifyLegends(this.membership, this._options.getCurrentUser()) &&
      this.hydrationError === null
    )
  }

  /**
   * Editing is only possible on the latest version, while nothing else is
   * running, and with permission.
   */
  @computed
  get isEditingDisabled() {
    return (
      !this.versionsVm.isViewingLatestVersion ||
      this.isBusy ||
      this.versionsVm.conflictVm.modal.showing ||
      !this.canModify
    )
  }

  /**
   * Whether opening the editor loads the legend again: when it was closed,
   * since someone else may have changed the legend meanwhile. Not while a
   * conflict waits for an answer, which reloads the legend itself.
   */
  get shouldRefetch() {
    return !this._lifecycle.active && !this.versionsVm.conflictVm.modal.showing
  }

  /**
   * The document as last saved, or as loaded.
   */
  @computed
  get lastSerialized() {
    return this._persister.lastSerialized
  }

  /**
   * Whether the legend changed since the editor opened.
   */
  @computed
  get isDirty() {
    return !!this._initialState && this.lastSerialized !== this._initialState
  }

  /**
   * Whether the selected version is the draft and changes to it wait to be
   * saved, or are saving.
   */
  @computed
  get isSaving() {
    return (
      !this._isShowingPublishedState &&
      (this.isBusy || this._persister.isSaving)
    )
  }

  /**
   * When the selected version was saved, or published.
   */
  @computed
  get saveStatusText() {
    // SAFETY: only shown once a version is loaded.
    const version = this.legendVersion!
    if (!this._isShowingPublishedState) {
      return `Saved ${formatRelativeTime(version.dateModified, this.now)}`
    }
    return version.datePublished
      ? `Published ${formatRelativeTime(version.datePublished, this.now)}`
      : ''
  }

  /**
   * How long the last save took.
   */
  @computed
  get saveLatencyTitle() {
    const latency = this._persister.lastSaveLatency
    return latency > 0
      ? `The save took ${(latency / 1000).toFixed(3)} seconds`
      : undefined
  }

  /**
   * Every builder, in symbol order: each sees the symbols declared by the
   * builders before it.
   */
  @computed
  get builders(): Builder[] {
    const options = this.optionsVm
    return [
      options,
      this.questionsVm,
      this.infoVm,
      options.deadlineConfig,
      options.titleConfig,
      options.txfConfig,
      options.pendingConfig,
      options.taxPlanConfig,
      options.personalOrganizerConfig,
      options.specsConfig,
      options.dataSources.routingContextSources,
      this.routingVm,
      this.templatesVm,
    ]
  }

  @computed
  get questions(): QuestionEditorViewModelBase[] {
    return [...this.questionsVm.allQuestions]
  }

  /**
   * The options tabs that validate, for the sidebar's error icons.
   */
  @computed
  get tabValidations(): Partial<Record<EditorTab, Validatable>> {
    return {
      General: this.optionsVm,
      Routing: this.routingVm,
      Info: this.infoVm,
    }
  }

  @computed
  get headerTab(): HeaderTab {
    return this.currentTab === 'Questions' ||
      this.currentTab === 'Templates' ||
      this.currentTab === 'Preview'
      ? this.currentTab
      : 'Options'
  }

  /**
   * The first validation error of the header tab, if any.
   *
   * @param tab
   */
  getHeaderTabError(tab: HeaderTab) {
    switch (tab) {
      case 'Options':
        return Object.values(this.tabValidations)
          .map((v) => firstError(v.validation))
          .find(Boolean)
      case 'Questions':
        return firstError(this.questionsVm.validation)
      case 'Templates':
        return firstError(this.templatesVm.validation)
      case 'Preview':
        return undefined
    }
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the legend's latest version, and what the builders offer to pick
   * from: the workspace's skills, roles, service items and bundles, and the
   * engine's symbols and organizer fields.
   */
  fetch = task(async (signal?: AbortSignal) => {
    // Reopening the editor would activate the old builders, which is slow for
    // a big legend. They're replaced once the version loads, so we leave them
    // out until then.
    this._unmanageBuilders()
    // A change saved when the editor closed is part of the latest version.
    await this._persister.flush()
    const store = this._options.legendStore
    // The symbols and organizer fields type the parts, which the version is
    // loaded into once they are known.
    const [latest] = await Promise.all([
      store.fetchLegendVersion({ legendId: this._options.legendId }, signal),
      store
        .fetchEngineSymbols(signal)
        .then((symbols) =>
          runInAction(() => (this.symbols.engineSymbols = symbols)),
        ),
      store
        .fetchOrganizerBindings(signal)
        .then((bindings) =>
          runInAction(() => (this.symbols.organizerBindings = bindings)),
        ),
    ])
    // Loading the version into the builders blocks the page for a while. If
    // the editor is still sliding in, that would stall the slide, so we wait.
    await this._options.waitForViewTransition()
    signal?.throwIfAborted()
    // The catalog only needs the legend's workspace, so it loads while the
    // version is loaded into the builders.
    const catalog = this.catalog.fetch(signal)
    await this.selectLegendVersion(latest.version, {}, signal)
    await catalog
  })

  /**
   * Whether the user may open the legend, judged from its latest version.
   * Throws `NotFound` when the legend doesn't exist.
   *
   * @param signal
   */
  async canOpen(signal?: AbortSignal) {
    const store = this._options.legendStore
    // When the legend opens from the legend list, the list has already loaded
    // its latest version, so this doesn't wait on a request. Listing the
    // versions otherwise still comes back long before `fetch` does.
    const latest =
      store.latestVersionFor(this._options.legendId) ??
      (
        await store.listLegendVersions(
          this._options.legendId,
          { limit: 1 },
          signal,
        )
      ).data.at(0)
    const membership = this._options
      .getCurrentUserWorkspaceMemberships()
      .find((m) => m.workspace.id === latest?.workspaceId)
    return (
      !!membership &&
      canViewWorkspaceLegends(membership, this._options.getCurrentUser())
    )
  }

  /**
   * Opens or closes the palette.
   */
  @action.bound
  togglePalette() {
    if (this.paletteModal.showing) {
      this.paletteModal.close()
    } else {
      this.paletteModal.show()
    }
  }

  /**
   * Shows the tab named in the URL, or the general options.
   *
   * @param tab
   */
  @action.bound
  showTabFromRoute(tab: string | undefined) {
    const tabs: EditorTab[] = [
      ...optionsTabs,
      'Questions',
      'Templates',
      'Preview',
    ]
    this._routeTab =
      tabs.find((t) => t.toLowerCase() === tab?.toLowerCase()) ?? 'General'
    this.setTab(this._routeTab)
  }

  /**
   * Switches tabs, validating the tab being left and the one being opened
   * once the opened one has painted. `Options` reopens the last options tab.
   *
   * @param tab
   */
  @action.bound
  setTab(tab: EditorTab | 'Options') {
    if (tab === this.currentTab) {
      return
    }

    const toValidate: Validatable[] = []
    const leaving = this.tabValidations[this.currentTab]
    if (leaving) {
      toValidate.push(leaving)
    }

    switch (tab) {
      case 'Questions':
        toValidate.push(this.questionsVm)
        this.currentTab = tab
        break
      case 'Templates':
        toValidate.push(this.templatesVm)
        this.currentTab = tab
        break
      case 'Preview':
        this.currentTab = tab
        break
      case 'Options':
        this.currentTab = this._optionsTab
        break
      default:
        this._optionsTab = tab
        this.currentTab = tab
    }

    // While the legend loads, the builders are about to be replaced. A tab
    // picked now gets validated like one the editor opened on.
    if (this.fetch.pending) {
      return
    }

    // Validating a big legend's questions takes long enough to hold up the
    // switch, so the errors show a frame after the tab does.
    void waitForPaint()
      .then(() => runInAction(() => toValidate.forEach(validateAll)))
      .catch(() => undefined)
  }

  /**
   * Shows the question picked in the palette by its client ID: closes the
   * palette and reveals the question on the Questions tab.
   *
   * @param cid
   */
  @action.bound
  jumpToQuestion(cid: string) {
    const question = this.questionsVm.allQuestions.find((q) => q.cid === cid)
    this.paletteModal.close()
    if (question) {
      this.setTab('Questions')
      this.questionsVm.reveal(question)
    }
  }

  /**
   * Goes back to the legend list.
   */
  @action.bound
  showLegendList() {
    this._options.navigate(links.legends(), { viewTransition: true })
  }

  /**
   * Loads a version of the legend into the builders; the latest one when no
   * version is given. Saving only runs while the latest version is selected.
   * When versions are selected one after the other, only the last one loads.
   *
   * @param version
   * @param options
   * @param signal
   * @returns The version, or `null` when another selection followed.
   */
  async selectLegendVersion(
    version: number | null,
    options: SelectLegendVersionOptions = {},
    signal?: AbortSignal,
  ) {
    // Changes pending for the selected version are saved to it first. A
    // reload of the latest version comes from within a save, which must not
    // wait on itself.
    if (version !== null && version !== this.legendVersion?.version) {
      await this._persister.flush()
    }

    this._selection.abort()
    const selection = new AbortController()
    this._selection = selection
    const selectionSignal = signal
      ? AbortSignal.any([selection.signal, signal])
      : selection.signal
    const legendVersion = await this._options.legendStore
      .fetchLegendVersion(
        {
          legendId: this._options.legendId,
          version: version ?? undefined,
        },
        selectionSignal,
      )
      .catch((err: unknown) => {
        if (selection.signal.aborted) {
          return null
        }
        throw err
      })
    if (!legendVersion || selection !== this._selection) {
      return null
    }

    this.symbols.clearGeneratedIds()
    runInAction(() => {
      this.legendVersion = legendVersion
      this.hydrate(legendVersion.document ?? emptyLegendDocument)
      this.questions.forEach((question) => question.toggleCollapsed(true))

      const serialized = this.serialize()
      if (!this._initialState || !options.keepInitialState) {
        this._initialState = serialized
      }
      const isEditable =
        this.versionsVm.isViewingLatestVersion || !this.versionsVm.latestVersion
      this._persister.start(
        isEditable
          ? {
              legendId: legendVersion.id,
              workspaceId: legendVersion.workspaceId,
              version: legendVersion.version,
              concurrencyVersion: legendVersion.concurrencyVersion,
              published: legendVersion.published,
            }
          : null,
        serialized,
      )
    })
    return legendVersion
  }

  /**
   * Rebuilds the builders from the document. A part that fails to load is
   * reported and makes the editor read-only, and the others still load.
   *
   * @param document
   */
  @action.bound
  hydrate(document: LegendDocument) {
    this._createBuilders()
    this.symbols.beginHydration()
    const errors: unknown[] = []
    const run = (hydratePart: () => void) => {
      try {
        hydratePart()
      } catch (err) {
        errors.push(err)
      }
    }
    run(() => this.questionsVm.hydrate(document.questions))
    run(() => this.templatesVm.hydrate(document.templates))
    run(() => this.routingVm.hydrate(document.routing))
    run(() => this.optionsVm.hydrate(document.options))
    run(() => this.infoVm.hydrate(document.infoLines))
    this.symbols.endHydration().forEach(run)
    this._manageBuilders()
    // Opening the tab validates it, which a tab already open doesn't get.
    if (this.currentTab === 'Templates') {
      validateAll(this.templatesVm)
    }

    this.hydrationError =
      errors.length > 0
        ? extractMessageFromError(errors[0]) || 'A part of it is invalid.'
        : null
    if (this.hydrationError !== null) {
      console.error(...errors)
      this._options
        .newAlert()
        .withState('error')
        .saying('Loading the legend failed', this.hydrationError)
        .autoDismiss(20_000)
        .show()
    }
  }

  /**
   * Validates every builder.
   *
   * @returns Whether the legend is valid.
   */
  validate() {
    return this.builders.map(validateAll).every(Boolean)
  }

  /**
   * The legend as the builders have it.
   */
  serialize(): LegendDocument {
    return {
      options: this.optionsVm.toJSON(),
      questions: this.questionsVm.toJSON(),
      templates: this.templatesVm.toJSON(),
      routing: this.routingVm.toJSON(),
      infoLines: this.infoVm.toJSON(),
    }
  }

  /**
   * Saves the whole document now, after the saves already queued.
   *
   * @param document
   * @returns The outcome; `null` when nothing was saved, such as when the
   * editor reloaded first, or someone else's changes were kept.
   */
  saveNow(document: LegendDocument) {
    return this._persister.save(document, { full: true })
  }

  /**
   * Saves now and checks the analyzer's verdict.
   *
   * @param versionNotes Saved with the version, for the publish that follows.
   * @returns Whether the legend is ready to publish.
   */
  persistAndCheck = task.resolved(async (versionNotes?: string) => {
    const showValidationError = () =>
      this._options
        .newAlert()
        .withState('error')
        .saying('There are validation errors.')
        .show()
        .autoDismiss()

    if (!this.validate()) {
      showValidationError()
      return false
    }

    const saved = await this._persister.save(this.serialize(), {
      full: true,
      versionNotes,
    })
    // Someone else's changes were kept, or the editor reloaded before the
    // save ran.
    if (!saved) {
      return false
    }
    const errors = this._getAnalyzerErrors(saved)
    if (errors.length > 0) {
      showValidationError()
      this._showAnalyzerErrors(errors)
      return false
    }
    return true
  })

  /**
   * Publishes the draft.
   *
   * @returns Whether it published; `false` when the legend didn't save,
   * didn't pass the checks, or publishing failed, which the alert reports.
   */
  private async _publish(versionNotes: string) {
    const isReady = await this.persistAndCheck(versionNotes)
    const legendVersion = this.legendVersion
    if (!isReady || !legendVersion) {
      return false
    }

    const alert = this._options
      .newAlert()
      .withState('pending')
      .saying('Publishing…')
      .show()
    const { version } = legendVersion
    try {
      await this._options.legendStore.publishLegendVersion({
        legendId: legendVersion.id,
        version,
        concurrencyVersion:
          this._persister.target?.concurrencyVersion ??
          legendVersion.concurrencyVersion,
        versionNotes,
      })
      await this.selectLegendVersion(version)
    } catch (err) {
      alert.fromError(err)
      return false
    }
    void this.versionsVm.refresh()
    alert
      .withState('success')
      .saying('Published', `Successfully published V${version}!`)
      .autoDismiss()
    return true
  }

  /**
   * Restores the legend as it was when the editor opened.
   */
  private async _discardLocalChanges() {
    // SAFETY: only offered once a version is loaded, which sets the initial
    // state too.
    const legendVersion = this.legendVersion!
    await this._persister.save(this._initialState!, { full: false })
    await this.selectLegendVersion(legendVersion.version).catch(
      this._options.createAlertErrorHandler('Loading the legend failed'),
    )
  }

  /**
   * Makes the draft an exact copy of the selected older version.
   */
  private async _revertToSelectedVersion() {
    // SAFETY: only offered while an older version is selected.
    const latestVersion = this.versionsVm.latestVersion!
    const selected = this.legendVersion!
    const version = latestVersion.published
      ? latestVersion.version + 1
      : latestVersion.version

    const alert = this._options
      .newAlert()
      .withState('pending')
      .saying(`Reverting to V${selected.version}…`)
      .show()
    try {
      await this._options.legendStore.saveLegendVersion({
        legendId: selected.id,
        workspaceId: selected.workspaceId,
        version,
        concurrencyVersion: latestVersion.concurrencyVersion,
        document: this.serialize(),
      })
      await this.selectLegendVersion(version, { keepInitialState: true })
      void this.versionsVm.refresh()
      alert
        .withState('success')
        .saying(
          'Reverted',
          `Successfully reverted draft to V${selected.version}`,
        )
        .autoDismiss()
    } catch (err) {
      alert.fromError(err)
      await this.selectLegendVersion(null)
      throw err
    }
  }

  /**
   * Replaces the builders. The old ones stop reacting; the new ones start once
   * {@link _manageBuilders} runs, after they are hydrated, so that loading
   * doesn't validate.
   */
  @action
  private _createBuilders() {
    this._unmanageBuilders()
    const dependencies = this._partDependencies
    this.questionsVm = new QuestionsBuilderViewModel({
      dependencies,
      parentQuestion: null,
    })
    this.templatesVm = new TemplatesBuilderViewModel(
      dependencies,
      () => this.questions,
    )
    this.optionsVm = new LegendOptionsViewModel(
      dependencies,
      () => this.versionsVm.latestPublishedVersion?.serviceType ?? null,
    )
    this.routingVm = new RoutingEditorViewModel(dependencies)
    this.infoVm = new InfoOptionsViewModel(dependencies)
  }

  private _manageBuilders() {
    for (const builder of this._managedBuilders) {
      this._lifecycle.manage(builder)
    }
  }

  private _unmanageBuilders() {
    for (const builder of this._managedBuilders) {
      this._lifecycle.remove(builder)
    }
  }

  private get _managedBuilders() {
    // The builders are unset while the constructor creates the first ones.
    return [
      this.questionsVm,
      this.templatesVm,
      this.optionsVm,
      this.routingVm,
      this.infoVm,
    ].filter((builder) => !!builder)
  }

  /**
   * Shows the draft that saving a change to the published version created.
   *
   * @param version
   */
  @action
  private _showCreatedDraft(version: number) {
    const draft = this._options.legendStore.legendVersionFor(
      this._options.legendId,
      version,
    )
    if (draft) {
      this.legendVersion = draft
    }
    void this.versionsVm.refresh()
  }

  /**
   * Whether the header shows when the version was published, rather than
   * whether changes are saved: for published and older versions.
   */
  private get _isShowingPublishedState() {
    return (
      !this.versionsVm.isViewingLatestVersion || !!this.legendVersion?.published
    )
  }

  /**
   * The analyzer's errors in a save, with paths into the document. Those of
   * the configs that don't show are left out, since they can't be fixed here.
   *
   * @param saved
   */
  private _getAnalyzerErrors(saved: SavedLegendVersion) {
    const hidden = this.optionsVm.hiddenConfigFields
    return mapAnalyzerErrorsToDocumentPaths(getAnalyzerErrors(saved)).filter(
      ({ field }) =>
        !hidden.some((name) => field === name || field.startsWith(`${name}.`)),
    )
  }

  /**
   * Marks the analyzer's errors on the parts they belong to.
   *
   * @param errors With paths into the document.
   */
  @action
  private _showAnalyzerErrors(errors: LegendAnalyzerError[]) {
    const {
      questions,
      routing,
      infoLines,
      templates,
      // Only the first story's questions are part of the document.
      stories: _stories,
      ...topLevel
    } = inflateAnalyzerErrors(errors)
    this.optionsVm.receiveErrors({ errors: [], inner: topLevel })
    this.infoVm.receiveErrors(infoLines)
    this.routingVm.receiveErrors(routing)
    this.questionsVm.receiveErrors(questions)
    this.templatesVm.receiveErrors(templates)
  }
}
